import { useState, useCallback } from 'react';
import { useAppDispatch } from '../app/hooks';
import { gqlClient } from '../components/AppProvider';
import { setToastConfig } from '../features/EDIContainer/EDIContainerSlice';
import { ToastSeverity } from '../util/Constants';
import { getErrorMessage } from '../util/Common';
import { mutationUpdateSEFBlobStorageVersion } from '../gql/MutationUpdateBlobStorageVersionActive';
import { 
    UpdateBlobStoragePayload,
    RequestResult 
} from '../gql-types.generated';

/**
 * Custom hook for setting a blob storage version as active
 * Handles the mutation and status management
 * 
 * @returns Object containing setVersionActive function, updateStatus, and isUpdating state
 */
export const useBlobStorageVersionSetActive = () => {
    const dispatch = useAppDispatch();
    const [isUpdating, setIsUpdating] = useState(false);
    const [updateStatus, setUpdateStatus] = useState<UpdateBlobStoragePayload | undefined>(undefined);

    /**
     * Sets a specific blob storage version as active
     * 
     * @param blobStorageId - The ID of the blob storage version to set as active
     */
    const setVersionActive = useCallback(async (blobStorageId: string) => {
        setIsUpdating(true);
        setUpdateStatus(undefined);
        
        try {
            const status = await mutationUpdateSEFBlobStorageVersion(gqlClient, blobStorageId);
            
            if (status) {
                if (status.result === RequestResult.Fail) {
                    dispatch(setToastConfig({
                        message: status.message as string,
                        severity: ToastSeverity.Error
                    }));
                    getErrorMessage(status.message as string, status.errors, true, true);
                } else if (status.result === RequestResult.Success) {
                    dispatch(setToastConfig({
                        message: status.message as string,
                        severity: ToastSeverity.Success
                    }));
                }
                setUpdateStatus(status);
            }
        } catch (e) {
            const errorMessage = (e as Error).message;
            dispatch(setToastConfig({
                message: errorMessage,
                severity: ToastSeverity.Error
            }));
            setUpdateStatus({
                result: RequestResult.Fail,
                message: errorMessage
            } as UpdateBlobStoragePayload);
        } finally {
            setIsUpdating(false);
        }
    }, [dispatch]);

    const clearUpdateStatus = useCallback(() => {
        setUpdateStatus(undefined);
    }, []);

    return { setVersionActive, updateStatus, isUpdating, clearUpdateStatus };
};